// HarfliGiris.js — GİRİŞ KAPISI: yalnız A–Z (20 Eyl 2026)
//
// 🔑 Serkan: *"Uygulamanın hiçbir yerinde İngilizce dışında karakter
//    girilemesin, girildiğinde alanın altında kırmızı uyarı çıksın,
//    kaydettirmesin."* · *"İngilizce karakter orada da zorunlu olsun"*
//    (isim alanları için istisna sorduk, istisna İSTENMEDİ).
//
// 🔴 NEDEN VAR: `up()` girişte Türkçe harfi SESSİZCE çeviriyordu — `ş` yazan
//    `S` kaydediyor ve bunu görmüyordu. 18 Eylül'de üç uçuş tam bu aileden
//    bir kusurla raporsuz arşivlendi (`İ` rapor fontunda yok, pdf-lib fırlattı).
//
// 🔑 NEDEN BİLEŞEN, NEDEN KANCA DEĞİL: 39 çağrı yerinin durum şekli birbirini
//    tutmuyor (`setX`, `setForm(p=>…)`, `.map()` içindeki satırlar). Bileşen
//    kendi uyarı durumunu KENDİ taşır; çağıran yere tek satırlık bir değişim
//    düşer ve döngü içinde de çalışır.
//
// ⚠️ HARF DÜŞÜRÜLMEZ, DEĞİŞİKLİK REDDEDİLİR. `ÇANKAYA` yapıştırılınca `Ç`yi
//    atıp `ANKAYA` yazmak sessiz veri bozmadır (İlke 2); onun yerine
//    yapıştırmanın TAMAMI reddedilir ve hangi harfin kabul edilmediği yazılır.
//    Böylece "kaydettirmesin" kendiliğinden sağlanır: geçersiz değer alana
//    hiç girmediği için kayda da gidemez — gözden kaçabilecek bir kaydet
//    kapısı KALMAZ (İlke 7).

import React, { forwardRef, useState } from 'react';
import { gecersizHarfler, upEvent } from './inputFormat';

// Uyarı bir KATMANDIR: layout'u itmez (her tuşta alanlar zıplamasın) ama
// altındaki etiketin üstüne bindiğinde okunur kalsın diye kendi zemini var.
const UYARI_STIL = {
  position: 'absolute', top: '100%', left: 0, zIndex: 5,
  fontSize: 10, lineHeight: 1.4, marginTop: 2, whiteSpace: 'nowrap',
  color: 'var(--red)', fontFamily: 'var(--mono)', letterSpacing: 0.3,
  background: 'var(--bg)', padding: '1px 5px', borderRadius: 3,
  border: '1px solid var(--red-soft)',
  pointerEvents: 'none',
};

// 🔴 UYARI ALANIN ALTINDA DURMALI (Serkan'ın sözü) ve LAYOUT'U İTMEMELİ.
//    İlk hâli sade bir kardeş elemandı; filtre satırı `display:flex` olduğu
//    için uyarı alanın ALTINA değil YANINA düştü (tarayıcıda görüldü, 20 Eyl).
//    Çözüm: alanın DIŞ ölçü özellikleri (genişlik/flex/kenar boşluğu) sarmalayıcıya
//    geçer — böylece alan satırdaki yerini aynen korur — ve uyarı sarmalayıcıya
//    göre MUTLAK konumlanır, yani her düzende altta çıkar ve hiçbir şeyi itmez.
const DIS_OLCU = new Set([
  'width', 'minWidth', 'maxWidth', 'flex', 'flexGrow', 'flexShrink', 'flexBasis',
  'margin', 'marginTop', 'marginRight', 'marginBottom', 'marginLeft',
  'gridColumn', 'gridRow', 'alignSelf', 'justifySelf',
]);

/** Uyarı metni — TEK CÜMLE, süssüz.
 *  🔑 Serkan (20 Eyl): *"only a-z değil, ONLY ENGLISH LETTERS — burdan anlar
 *     artık."* · *"not accepted'a gerek yok, only english letters bu kadar."*
 *     Hangi harfin reddedildiğini saymıyoruz: zaten alana girmedi, kişi ne
 *     yazdığını biliyor. Kısa uyarı okunur, uzun uyarı okunmaz. */
export const RED_METNI = 'ONLY ENGLISH LETTERS';

/**
 * A–Z dışı harfi kabul etmeyen metin girişi.
 *
 * `onChange` EVENT değil DEĞER alır — çağrı yerleri `upEvent(e)` sarmasından
 * kurtulur ve tek kapı burası olur.
 *
 * @param {'input'|'textarea'} as  çizilecek etiket
 * @param {string} value           kontrollü değer
 * @param {(v:string)=>void} onChange
 */
// 🔴 `forwardRef` ŞART: çağrı yerlerinden biri (NavLog RVSM kutuları) `ref`
//    veriyor ve beş hane dolunca SONRAKİ kutuya odaklanıyor. Ref iletilmezse
//    React uyarır, `ref.current` boş kalır ve o otomatik odak SESSİZCE ölür.
const HarfliGiris = forwardRef(function HarfliGiris(
  { as = 'input', value, onChange, style, ...rest }, ref) {
  const [red, setRed] = useState(null);
  const Etiket = as;

  const disStil = {};
  const icStil = {};
  for (const [k, v] of Object.entries(style || {})) {
    (DIS_OLCU.has(k) ? disStil : icStil)[k] = v;
  }
  // Dış ölçü sarmalayıcıya geçtiyse alan onu DOLDURUR; geçmediyse kendi
  // doğal genişliğinde kalır (sarmalayıcı da ona göre büzülür).
  const olcuVar = disStil.width != null || disStil.flex != null || disStil.flexBasis != null;

  // 🔴 SABİT GENİŞLİK EZİLMESİN (tarayıcıda görüldü, 20 Eyl): `<input>`in
  //    doğal bir asgari genişliği vardır (`size` özniteliği), `<span>`in YOKTUR.
  //    Kalabalık bir flex satırında sarmalayıcı bu yüzden büzülüyor ve 70 px'lik
  //    DEP kutusu 26 px'e düşüyordu. Piksel genişliği AÇIKÇA verilmiş bir alan
  //    "tam bu kadar" demektir; çağıran flex davranışı belirtmediyse büzülmez.
  if (typeof disStil.width === 'number'
      && disStil.flexShrink == null && disStil.flex == null) {
    disStil.flexShrink = 0;
  }

  const degisti = (e) => {
    const el = e.target;
    const ham = String(el.value ?? '');
    const kotu = gecersizHarfler(ham);
    if (kotu.length) {
      // DEĞİŞİKLİĞİ GERİ AL — alan olduğu gibi kalır.
      const eski = String(value ?? '');
      const fark = ham.length - eski.length;
      const nokta = Math.max(0, (el.selectionStart ?? eski.length) - Math.max(0, fark));
      el.value = eski;
      try { el.setSelectionRange(nokta, nokta); } catch { /* seçim desteklemeyen tip */ }
      setRed(kotu);
      return;
    }
    if (red) setRed(null);
    onChange(upEvent(e));
  };

  return (
    <span style={{ position: 'relative', display: 'inline-block', verticalAlign: 'top', ...disStil }}>
      <Etiket
        ref={ref}
        {...rest}
        style={olcuVar ? { ...icStil, width: '100%', boxSizing: 'border-box' } : icStil}
        value={value ?? ''}
        onChange={degisti}
      />
      {red && (
        <span style={UYARI_STIL} data-testid="harf-uyari" role="alert">
          {RED_METNI}
        </span>
      )}
    </span>
  );
});

export default HarfliGiris;
